import { IMember } from "Types/member";
import { DateTime } from "luxon";
import { makeAutoObservable } from "mobx";
import Limit, { ILimits } from "./limit";
import { setPlanFeatures } from 'App/utils/planFeatures';

export interface IAccount extends IMember {
  changePassword?: any;
  limits: ILimits;
  banner: string;
  email: string;
  verifiedEmail: string;
  id: string;
  smtp: boolean;
  license: string;
  expirationDate?: number;
  permissions: string[];
  settings: any;
  iceServers: string;
  hasPassword: boolean;
  apiKey: string;
  tenantKey: string;
  edition: string;
  optOut: string;
  versionNumber: string;
  plan: {
    type: undefined;
    remainingSessions: undefined;
    billingSetup: false;
    remainingTrialDays: undefined;
    features: []
  };
}

export default class Account {
  changePassword: boolean;
  limits: Limit[] = [];
  banner: boolean;
  email: string;
  verifiedEmail: boolean;
  id: string;
  smtp: false;
  license: string;
  expirationDate: DateTime;
  permissions: string[] = [];
  settings: Record<string, any> = {};
  iceServers: any[] = [];
  hasPassword: boolean;
  apiKey: string;
  tenantKey: string;
  tenantName: string;
  edition: string;
  optOut: boolean;
  versionNumber: string;
  tenantId: string;
  name: string;
  createdAt: DateTime;
  admin: boolean;
  superAdmin: boolean;
  joined: boolean;
  expiredInvitation: boolean;
  roleId: string;
  roleName: string;
  invitationLink: string;
  plan: {
    type: undefined;
    remainingSessions: undefined;
    billingSetup: false;
    remainingTrialDays: undefined;
    features: []
  };

  constructor(account: Partial<IAccount> = {}) {
    Object.assign(this, {
      ...account,
      id: account.id || account.userId,
      expirationDate: DateTime.fromMillis(account.expirationDate ?? 0),
    });
    setPlanFeatures(account.plan?.features);
    makeAutoObservable(this);
  }

  get hasVideoExport() {
    if (!this.settings || !this.settings.modules) {
      return false;
    }
    const hasModule = this.settings.modules?.includes("replay-export");
    return hasModule;
  }

  get hasExportPermission() {
    const hasPermission = this.permissions.includes("SESSION_EXPORT");
    const isAdmin = this.admin || this.superAdmin;
    return this.hasVideoExport && (hasPermission || isAdmin);
  }

  toData = () => ({
    id: this.id,
    email: this.email,
    verifiedEmail: this.verifiedEmail,
    smtp: this.smtp,
    license: this.license,
    expirationDate: this.expirationDate.toMillis(),
    permissions: this.permissions,
    settings: this.settings,
    iceServers: this.iceServers,
    hasPassword: this.hasPassword,
    apiKey: this.apiKey,
    tenantKey: this.tenantKey,
    edition: this.edition,
    optOut: this.optOut,
    versionNumber: this.versionNumber,
    name: this.name,
    createdAt: this.createdAt,
    admin: this.admin,
    superAdmin: this.superAdmin,
    joined: this.joined,
    expiredInvitation: this.expiredInvitation,
    roleId: this.roleId,
    roleName: this.roleName,
    invitationLink: this.invitationLink,
  });
}
